import {
  Card,
  Collapse,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useFormikContext } from "formik";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import { ICampaign } from "@/interfaces/campaign.interface";
import { CampaignsMessages } from "@/messages/campaigns/campaigns.messages";
import { CampaignStatus, CampaignType } from "@/types/campaign.types";
import { FilterType, IFilter } from "@/types/filter.types";

import { CampaignFilterKey } from "../../types";

const GRID_SIZES = {
  PRIMARY_FIELD: { lg: 3, md: 4, sm: 6, xs: 12 },
  SECONDARY_FIELD: { lg: 6, md: 6, sm: 12, xs: 12 },
  BUTTON: { lg: 2, md: 4, sm: 6, xs: 12 },
  FULL_WIDTH: { lg: 12, md: 12, sm: 12, xs: 12 },
} as const;

const FILTER_CONSTANTS = {
  NOT_FOUND_INDEX: -1,
  EMPTY_STRING: "",
  DEFAULT_SPACING: 2,
  EXPANDED_MARGIN_TOP: 2,
} as const;

const SELECT_PLACEHOLDERS = {
  TYPE: "Selecione o tipo",
  STATUS: "Selecione o status",
} as const;

const CAMPAIGN_TYPE_OPTIONS = [
  { key: "promotional", value: CampaignType.PromotionalPrice },
  { key: "discount", value: CampaignType.DiscountCoupon },
  { key: "simple", value: CampaignType.SimpleMessage },
] as const;

const CAMPAIGN_STATUS_OPTIONS = [
  { key: "active", value: CampaignStatus.Active },
  { key: "inactive", value: CampaignStatus.Inactive },
  { key: "draft", value: CampaignStatus.Draft },
  { key: "rejected", value: CampaignStatus.Rejected },
  { key: "hold", value: CampaignStatus.Hold },
  { key: "completed", value: CampaignStatus.Completed },
] as const;

type Props = {
  messages: CampaignsMessages;
};

class FilterManager {
  private static findFilterByProperty(
    property: CampaignFilterKey,
    filters: IFilter<ICampaign>[]
  ): IFilter<ICampaign> | undefined {
    return filters.find((filter) => filter.property === property);
  }

  private static isValueEmpty(value: string | number | boolean | null): boolean {
    return (
      value === null ||
      value === FILTER_CONSTANTS.EMPTY_STRING ||
      (typeof value === "number" && isNaN(value))
    );
  }

  private static removeExistingFilter(
    filters: IFilter<ICampaign>[],
    property: CampaignFilterKey
  ): IFilter<ICampaign>[] {
    const existingFilterIndex = filters.findIndex((filter) => filter.property === property);
    if (existingFilterIndex !== FILTER_CONSTANTS.NOT_FOUND_INDEX) {
      filters.splice(existingFilterIndex, 1);
    }
    return filters;
  }

  private static createNewFilter(
    property: CampaignFilterKey,
    value: string | number | boolean,
    filterType: FilterType
  ): IFilter<ICampaign> {
    return {
      property,
      filterType,
      value,
    };
  }

  static updateFilters(
    currentFilters: IFilter<ICampaign>[],
    property: CampaignFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ): IFilter<ICampaign>[] {
    const updatedFilters = this.removeExistingFilter([...currentFilters], property);

    if (this.isValueEmpty(value)) {
      return updatedFilters;
    }

    const newFilter = this.createNewFilter(
      property,
      value as string | number | boolean,
      filterType
    );
    return [...updatedFilters, newFilter];
  }

  static getFilterValue(property: CampaignFilterKey, filters: IFilter<ICampaign>[]): string {
    const filter = this.findFilterByProperty(property, filters);
    return filter?.value !== undefined && filter?.value !== null
      ? String(filter.value)
      : FILTER_CONSTANTS.EMPTY_STRING;
  }
}

class ErrorManager {
  static getErrorForProperty(property: string, errors: unknown) {
    if (Array.isArray(errors)) {
      return errors.find((error) => error?.property === property);
    }
    return undefined;
  }

  static hasError(property: CampaignFilterKey, errors: unknown): boolean {
    const error = this.getErrorForProperty(String(property), errors);
    return !!error && !!error.value;
  }

  static getErrorText(property: CampaignFilterKey, errors: unknown): string | undefined {
    const error = this.getErrorForProperty(String(property), errors);
    return error?.value;
  }
}

function CampaignFiltersForm({ messages }: Props) {
  const { values, setValues, submitForm, errors } = useFormikContext<IFilter<ICampaign>[]>();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const handleFilterChange = (
    property: CampaignFilterKey,
    value: string | number | boolean | null,
    filterType: FilterType
  ) => {
    setValues((prevFilters) =>
      FilterManager.updateFilters(prevFilters, property, value, filterType)
    );
  };

  const handleStatusChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string;
    handleFilterChange("status", selectedValue || null, FilterType.Equals);
  };

  const handleTypeChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string;
    const typeValue =
      selectedValue === FILTER_CONSTANTS.EMPTY_STRING ? null : Number(selectedValue);
    handleFilterChange("type", typeValue, FilterType.Equals);
  };

  const handleClearFilters = () => {
    setValues([]);
  };

  const handleToggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  const getStatusValue = () => FilterManager.getFilterValue("status", values);
  const getTypeValue = () => FilterManager.getFilterValue("type", values);

  const renderTextField = (
    name: string,
    label: string,
    property: CampaignFilterKey,
    filterType: FilterType = FilterType.Contains
  ) => (
    <TextField
      name={name}
      label={label}
      onChange={(e) => handleFilterChange(property, e.target.value, filterType)}
      value={FilterManager.getFilterValue(property, values)}
      error={ErrorManager.hasError(property, errors)}
      helperText={ErrorManager.getErrorText(property, errors)}
      fullWidth
      slotProps={{
        inputLabel: { shrink: true },
      }}
    />
  );

  const renderSelectField = (
    property: CampaignFilterKey,
    label: string,
    value: string,
    onChange: (event: SelectChangeEvent<unknown>) => void,
    children: React.ReactNode
  ) => (
    <FormControl fullWidth error={ErrorManager.hasError(property, errors)}>
      <InputLabel shrink>{label}</InputLabel>
      <Select value={value} onChange={onChange} label={label} displayEmpty>
        {children}
      </Select>
      {ErrorManager.getErrorText(property, errors) && (
        <FormHelperText>{ErrorManager.getErrorText(property, errors)}</FormHelperText>
      )}
    </FormControl>
  );

  const renderDatePicker = (
    property: CampaignFilterKey,
    label: string,
    filterType: FilterType = FilterType.GreaterThan
  ) => (
    <DatePicker
      label={label}
      value={dayjs((FilterManager.getFilterValue(property, values) as string) || null)}
      onChange={(value) =>
        handleFilterChange(property, value ? value.toISOString() : null, filterType)
      }
      slotProps={{
        textField: {
          helperText: ErrorManager.getErrorText(property, errors),
          error: Boolean(ErrorManager.getErrorText(property, errors)),
          fullWidth: true,
          InputLabelProps: { shrink: true },
        },
      }}
    />
  );

  const renderTypeSelectOptions = () => [
    <MenuItem key="empty" value={FILTER_CONSTANTS.EMPTY_STRING}>
      {SELECT_PLACEHOLDERS.TYPE}
    </MenuItem>,
    ...CAMPAIGN_TYPE_OPTIONS.map(({ key, value }) => (
      <MenuItem key={key} value={String(value)}>
        {key === "promotional"
          ? messages.campaignTypePromotionalPrice
          : key === "discount"
            ? messages.campaignTypeDiscountCoupon
            : messages.campaignTypeSimpleMessage}
      </MenuItem>
    )),
  ];

  const renderStatusSelectOptions = () => [
    <MenuItem key="empty-status" value={FILTER_CONSTANTS.EMPTY_STRING}>
      {SELECT_PLACEHOLDERS.STATUS}
    </MenuItem>,
    ...CAMPAIGN_STATUS_OPTIONS.map(({ key, value }) => (
      <MenuItem key={key} value={value}>
        {messages[`status${key.charAt(0).toUpperCase() + key.slice(1)}` as keyof CampaignsMessages]}
      </MenuItem>
    )),
  ];

  const renderFilterButtons = () => (
    <Grid
      container
      spacing={FILTER_CONSTANTS.DEFAULT_SPACING}
      mt={isExpanded ? FILTER_CONSTANTS.EXPANDED_MARGIN_TOP : 0}
    >
      <Grid size={GRID_SIZES.BUTTON}>
        <MDButton variant="outlined" color="dark" onClick={handleToggleExpanded} fullWidth>
          {isExpanded ? messages.lessFiltersButton : messages.moreFiltersButton}
        </MDButton>
      </Grid>
      <Grid size={GRID_SIZES.BUTTON}>
        <MDButton variant="gradient" color="light" onClick={handleClearFilters} fullWidth>
          {messages.clearFilterButton}
        </MDButton>
      </Grid>
      <Grid size={GRID_SIZES.BUTTON}>
        <MDButton variant="gradient" color="dark" onClick={submitForm} fullWidth>
          {messages.filterButton}
        </MDButton>
      </Grid>
    </Grid>
  );

  return (
    <Card>
      <MDBox p={FILTER_CONSTANTS.DEFAULT_SPACING}>
        <Grid container spacing={FILTER_CONSTANTS.DEFAULT_SPACING} alignItems="stretch" wrap="wrap">
          <Grid size={GRID_SIZES.PRIMARY_FIELD}>
            {renderTextField("code", messages.campaignCodeColumn, "code")}
          </Grid>

          <Grid size={GRID_SIZES.PRIMARY_FIELD}>
            {renderTextField("name", messages.campaignNameColumn, "name")}
          </Grid>

          <Grid size={GRID_SIZES.PRIMARY_FIELD}>
            {renderSelectField(
              "type",
              messages.campaignTypeColumn,
              getTypeValue(),
              handleTypeChange,
              renderTypeSelectOptions()
            )}
          </Grid>

          <Grid size={GRID_SIZES.PRIMARY_FIELD}>
            {renderSelectField(
              "status",
              messages.statusColumn,
              getStatusValue(),
              handleStatusChange,
              renderStatusSelectOptions()
            )}
          </Grid>

          <Grid size={GRID_SIZES.FULL_WIDTH}>
            <Collapse in={isExpanded} unmountOnExit>
              <Grid container spacing={FILTER_CONSTANTS.DEFAULT_SPACING}>
                <Grid size={GRID_SIZES.SECONDARY_FIELD}>
                  {renderDatePicker("startDate", messages.startValidityDateColumn)}
                </Grid>

                <Grid size={GRID_SIZES.SECONDARY_FIELD}>
                  {renderDatePicker("endDate", messages.endValidityDateColumn)}
                </Grid>
              </Grid>
            </Collapse>

            {renderFilterButtons()}
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}

export default CampaignFiltersForm;
